<script lang="ts" setup>
	import dayjs from 'dayjs'
	import useClaimsApi from '~/services/apis/useClaimsApi'

	const props = defineProps<{
		isPending: boolean
		planDetails?: PolicyResponse | null
	}>()
	const { fetchClaims } = useClaimsApi()

	const claimsDetails = ref<ClaimsMetaInformation | null>(null)
	const fetching = shallowRef(false)
	const { internationalNoFormat } = useCurrency()
	const { productCategoryType } = useUtils()

	defineEmits<{
		(event: 'activate'): void
		(event: 'reminder'): void
		(event: 'make-claim'): void
	}>()
	const activePlan = computed(() => {
		return props.planDetails
	})
	const policyNumber = computed(
		() =>
			activePlan.value?.policy_number ||
			activePlan.value?.meta?.policy_number ||
			activePlan.value?.meta?.sdk_config?.policy_number
	)
	const planCategoryName = computed(() => activePlan.value?.product?.productCategory?.name?.toLowerCase() || '')
	const planName = computed(() => activePlan.value?.product?.name || '')
	const categoryType = computed(() =>
		productCategoryType(planCategoryName.value, activePlan.value?.product?.code_name)
	)

	const planLabel = computed(() => {
		const label = categoryType.value?.isAuto
			? 'Vehicle'
			: planCategoryName.value !== 'other'
				? planCategoryName.value
				: planName.value
		return label
	})
	const policyPlaceholder = computed(() => {
		if (props.isPending) {
			return `This ${planLabel.value} has not been
									activated yet. Activate ${planLabel.value} now
									to enjoy full benefits.`
		} else if (!activePlan.value?.meta?.vehicleInspectionMeta && !categoryType.value?.isThirdParty) {
			return `This ${planLabel.value} has not been
									inspected yet. Inspect ${planLabel.value} now
									to be able to process claims.`
		} else if (!claimsDetails.value?.incident_type) {
			return 'No claims Has been Made yet'
		}
		return ''
	})

	const claimStatus = computed(() => claimsDetails.value?.claim_status?.toLowerCase() || '')

	watch(
		policyNumber,
		n => {
			if (n) {
				resolve(async () => {
					const res = await fetchClaims({
						policy_id: activePlan.value?.id as string
					})
					if (res?.items) {
						claimsDetails.value = res.items[0]
					}
				}, fetching)
			}
		},
		{ immediate: true }
	)
</script>

<template>
	<div>
		<loader-skeleton v-if="fetching" :height="50" class-list="mt-5" :max-width="false" />
		<template v-else>
			<my-cover-policy-label
				v-show="!isPending"
				:label="`Claims made on this ${planLabel} so far`"
				:icon="{ name: 'bold-note', dir: 'documents' }"
			/>
			<my-cover-pending-policy-placeholder
				v-if="!claimsDetails?.incident_type || isPending"
				icon-dir="documents"
				title="No Claims Yet"
				path-class="fill-gray-200"
				:sub-title="policyPlaceholder"
				icon-name="note"
				:is-pending="isPending || !claimsDetails"
				use-reminder
				:use-btn="
					(categoryType?.isGadget && !claimsDetails && !isPending) ||
					(categoryType?.isAuto && Boolean(activePlan?.meta?.claimLink))
				"
				@activate="$emit('activate')"
				@reminder="$emit('reminder')"
			>
				<template #button-group>
					<span />
				</template>
			</my-cover-pending-policy-placeholder>
			<template v-else>
				<div
					class="mt-5 flex min-h-[50px] w-full grid-cols-2 items-center gap-x-3 rounded border border-purple-50 bg-purple-25 px-5 text-center"
				>
					<div class="border- w-full text-center">
						<span class="text-sm text-gray-600"> Claims Paid: </span>
						<span v-if="claimsDetails?.offer_amount" class="font-medium text-gray-700">
							₦{{ internationalNoFormat(claimsDetails?.offer_amount || 0) }}
						</span>
						<span v-else class="text-xs italic text-gray-400">In Progress</span>
					</div>
				</div>

				<ul class="mt-5">
					<li
						class="flex min-h-[90px] w-full items-center justify-between border border-purple-50 bg-purple-25 px-5 py-3 tracking-wide"
					>
						<div class="flex items-center gap-x-3">
							<div
								class="flex h-[32px] w-[32px] flex-none items-center justify-center rounded-full bg-purple-100"
							>
								<lazy-tools-icon dir="fill" icon="accidental-damage" />
							</div>

							<div class="space-y-1">
								<h3 class="text-sm font-medium text-gray-600">
									{{ claimsDetails?.incident_type || 'N/A' }}
								</h3>
								<p class="text-xs">
									Lodged:
									{{
										(claimsDetails?.created_at &&
											dayjs(claimsDetails?.created_at).format('DD MMM, YYYY')) ||
										''
									}}
								</p>
							</div>
						</div>

						<div class="flex flex-col">
							<shared-badge
								:text="claimsDetails?.claim_status || ''"
								class="mb-1 inline-flex text-xs"
								:active="!['declined', 'pending'].includes(claimStatus)"
								:pending="claimStatus === 'pending'"
								:ongoing="claimStatus === 'ongoing'"
							/>

							<nuxt-link
								:to="{
									path: `/claims/details-${claimsDetails?.id || ''}`
								}"
								class="py-1.5 text-center text-xs text-gray-500 transition-all hover:text-primary-2"
							>
								View Details
								<hr
									class="mx-auto mt-1 max-w-[80px] border-b border-dotted border-gray-300 outline-none"
								/>
							</nuxt-link>
						</div>
					</li>
				</ul>
			</template>
		</template>
	</div>
</template>
